import * as React from "react";
import { useParams, Link } from "react-router-dom";
import { AlertTriangle, AlertCircle, Info, X, ChevronRight, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * Compact alerts panel — used on the Dashboard.
 * Shows up to N open alerts, most-severe first, with a click-through
 * that navigates to the target module entity.
 */
const SEV = {
  alert: { icon: AlertCircle, dot: "bg-red-500", label: "Alert", tone: "text-red-700" },
  warn:  { icon: AlertTriangle, dot: "bg-amber-500", label: "Warn", tone: "text-amber-700" },
  info:  { icon: Info, dot: "bg-brand-violet", label: "Info", tone: "text-brand-violet-lo" },
};

const ORDER = { alert: 0, warn: 1, info: 2 };

function entityHref(companyId, ev) {
  const t = ev.target_module;
  const id = ev.entity_id;
  if (!id) return `/c/${companyId}/alerts`;
  if (t === "bookings") return `/c/${companyId}/m/bookings?open=${id}`;
  if (t === "rotas")    return `/c/${companyId}/m/rotas`;
  if (t === "pay")      return `/c/${companyId}/m/pay?open=${id}`;
  if (t === "books")    return `/c/${companyId}/m/books?open=${id}`;
  return `/c/${companyId}/alerts`;
}

export default function AlertsPanel({ limit = 5, showEmpty = true }) {
  const { companyId } = useParams();
  const [data, setData] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/companies/${companyId}/alerts`, { params: { limit: 30 } });
      setData(data);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  }, [companyId]);

  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => {
    const on = (e) => { const t = e.detail?.type;
      if (t && t.startsWith("rule_event.")) load();
    };
    window.addEventListener("fable:event", on);
    return () => window.removeEventListener("fable:event", on);
  }, [load]);

  async function dismiss(ev) {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/companies/${companyId}/alerts/${ev.id}/dismiss`, {});
      toast.success("Dismissed");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  if (!data) return <div className="text-[12px] text-brand-muted" data-testid="alerts-loading">Loading…</div>;
  const rows = [...data.alerts]
    .sort((a, b) => (ORDER[a.severity] - ORDER[b.severity]) || (b.created_at.localeCompare(a.created_at)))
    .slice(0, limit);

  if (rows.length === 0) {
    if (!showEmpty) return null;
    return (
      <div className="warm-empty p-6 text-center" data-testid="alerts-empty">
        <CheckCircle2 size={20} className="text-emerald-600 mx-auto mb-2"/>
        <div className="text-[13px] font-semibold text-brand-ink">All clear</div>
        <div className="text-[11.5px] text-brand-muted mt-0.5">No operational alerts.</div>
      </div>
    );
  }

  return (
    <div className="card-panel" data-testid="alerts-panel">
      <div className="card-panel-head">
        <div>
          <div className="micro-label">Operational alerts</div>
          <div className="mt-0.5 text-[14px] font-semibold text-brand-ink">
            {data.counts.total} open
            <span className="ml-2 mono-num text-[10.5px] text-brand-muted">
              {data.counts.alert}A · {data.counts.warn}W · {data.counts.info}i
            </span>
          </div>
        </div>
        <Link to={`/c/${companyId}/alerts`} className="btn-outline !h-7" data-testid="alerts-open-page">
          Open Alerts <ChevronRight size={12}/>
        </Link>
      </div>
      <div className="divide-y divide-brand-rule">
        {rows.map((ev) => {
          const s = SEV[ev.severity] || SEV.info;
          const Icon = s.icon;
          return (
            <div key={ev.id} className="px-4 py-2.5 flex items-start gap-3 hover:bg-brand-hush"
                 data-testid={`alert-row-${ev.id}`}>
              <span className={`mt-0.5 h-2 w-2 rounded-full ${s.dot}`}/>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="mono-num text-[10px] chip">{ev.rule_id}</span>
                  <span className={`text-[12.5px] font-medium ${s.tone}`}>{ev.title}</span>
                </div>
                <div className="mono-num text-[10.5px] text-brand-muted mt-0.5">
                  {ev.created_at.replace('T', ' ').slice(0,16)} · target: {ev.target_module}
                </div>
              </div>
              <Link to={entityHref(companyId, ev)}
                    className="btn-ghost !h-7 !px-2 shrink-0"
                    data-testid={`alert-open-${ev.id}`}>
                <ChevronRight size={13}/>
              </Link>
              <button className="btn-ghost !h-7 !px-2 shrink-0" onClick={() => dismiss(ev)}
                      disabled={busy}
                      data-testid={`alert-dismiss-${ev.id}`}>
                <X size={13}/>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
